import { getNetworkStatus } from '@/lib/network-status';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import type {
  HangupVerificationStatus,
  VoiceCallStateStatus,
} from '@packages/client-sdk-react/src/voice/voice-call-container';
import { useVoiceCallState } from '@packages/client-sdk-react/src/voice/voice-call-container';
import {
  getVonageSessionDiagnostics,
  useVonageClient,
} from '@packages/client-sdk-react/src/vonage-client-provider';
import { useCallback, useEffect, useRef } from 'react';

const log = createLogger('CallHealthMonitor', ['calls']);

const REGISTRY_CHECK_INTERVAL_MS = 15_000;
const DESYNC_CONFIRM_DELAY_MS = 1_000;
const DESYNC_OUTCOME_TIMEOUT_MS = 30_000;
const DESYNC_RECOVERY_POLL_INTERVAL_MS = 5_000;

const ACTIVE_CALL_STATES: VoiceCallStateStatus[] = [
  'in-progress',
  'ringing',
  'hangingup',
  'reconnectable',
];

// Registry-desync check is only meaningful once the PC has been established.
// During `ringing` the SDK has a callId but the PC is created later during SDP
// negotiation, so `getPeerConnection` legitimately returns null and would
// produce false-positive desync reports.
const DESYNC_CHECK_STATES: VoiceCallStateStatus[] = ['in-progress', 'hangingup', 'reconnectable'];

type DesyncOutcome = 'recovered' | 'completed_without_pc_recovery' | 'stuck' | 'call_changed';

type DesyncOutcomeTracker = {
  desyncId: string;
  callId: string;
  startedAt: number;
  pcRecovered: boolean;
  resolved: boolean;
  timeoutId: ReturnType<typeof setTimeout>;
  recoveryIntervalId: ReturnType<typeof setInterval>;
};

/**
 * Monitors SDK call registry and WebRTC peer connection health during active calls.
 *
 * Detects when the Vonage SDK silently loses a call from its internal registry
 * (getPeerConnection returns null while app still has a callId). This is the
 * root cause of phantom calls — the SDK can no longer terminate the server-side leg.
 *
 * Also listens to RTCPeerConnection state events for supporting diagnostic context.
 *
 * Desync emission is a two-stage signal:
 * 1. `call:health:desync` fires at WARN when the registry returns null. No
 *    direct Sentry routing — most detections turn out to be transient (SDK
 *    mid-reconnect, cleanup race around user hangup).
 * 2. `call:health:desync:outcome` resolves the detection when the call
 *    reaches `idle`, the call is replaced, or a 30s timeout elapses. Only
 *    `stuck` outcomes log at ERROR (Sentry). `recovered` and
 *    `completed_without_pc_recovery` stay at INFO. Query-time join on
 *    `desyncId` to compute true phantom rate.
 */
export const useCallHealthMonitor = () => {
  const [call] = useVoiceCallState();
  const vonageClient = useVonageClient();

  const desyncReportedRef = useRef(false);
  const callIdRef = useRef(call.callId);
  const callStateRef = useRef(call.callState);
  const hangupVerificationStatusRef = useRef<HangupVerificationStatus>(
    call.hangupVerification.status,
  );
  const isActiveRef = useRef(false);
  const lastPcStatesRef = useRef<{
    iceConnectionState?: string;
    connectionState?: string;
    signalingState?: string;
  }>({});
  const lastPcStateChangeAtRef = useRef<number | null>(null);
  const pcEverAttachedRef = useRef(false);
  const callStateEnteredAtRef = useRef(Date.now());
  const callStartedAtRef = useRef<{ callId: string | undefined; startedAt: number }>({
    callId: undefined,
    startedAt: Date.now(),
  });
  const outcomeTrackerRef = useRef<DesyncOutcomeTracker | null>(null);

  const callId = call.callId;
  const callState = call.callState;
  const isActive = !!callId && ACTIVE_CALL_STATES.includes(callState);
  const isDesyncCheckActive = !!callId && DESYNC_CHECK_STATES.includes(callState);

  // Keep refs in sync
  callIdRef.current = callId;
  if (callStateRef.current !== callState) {
    callStateEnteredAtRef.current = Date.now();
  }
  callStateRef.current = callState;
  hangupVerificationStatusRef.current = call.hangupVerification.status;
  isActiveRef.current = isActive;

  if (callId && callStartedAtRef.current.callId !== callId) {
    callStartedAtRef.current = { callId, startedAt: Date.now() };
  }

  // Stable identity (refs only, no closed-over state). Both the idle/call-changed
  // effects and the setTimeout inside the desync-check effect depend on this —
  // useCallback makes that stability explicit rather than relying on inline
  // "happens to only read refs" discipline.
  const emitOutcome = useCallback((outcome: DesyncOutcome): void => {
    const tracker = outcomeTrackerRef.current;
    if (!tracker || tracker.resolved) {
      return;
    }
    tracker.resolved = true;
    clearTimeout(tracker.timeoutId);
    clearInterval(tracker.recoveryIntervalId);
    outcomeTrackerRef.current = null;

    const payload = {
      desyncId: tracker.desyncId,
      deviceLegId: tracker.callId,
      outcome,
      pcRecovered: tracker.pcRecovered,
      finalCallState: callStateRef.current,
      finalHangupStatus: hangupVerificationStatusRef.current,
      msSinceDesync: Date.now() - tracker.startedAt,
      timestamp: Date.now(),
    };

    if (outcome === 'stuck') {
      log.error('SDK call registry desync outcome', payload);
    } else {
      log.info('SDK call registry desync outcome', payload);
    }

    const tracingLevel = outcome === 'stuck' ? CallEventLevel.Error : CallEventLevel.Info;
    tracingService.trackEvent(CallEventName.CallHealthDesyncOutcome, tracingLevel, payload);
  }, []);

  // Resolve outcome when call naturally ends.
  useEffect(() => {
    if (callState !== 'idle') {
      return;
    }
    const tracker = outcomeTrackerRef.current;
    if (tracker && !tracker.resolved) {
      emitOutcome(tracker.pcRecovered ? 'recovered' : 'completed_without_pc_recovery');
    }
    desyncReportedRef.current = false;
    lastPcStatesRef.current = {};
    lastPcStateChangeAtRef.current = null;
    pcEverAttachedRef.current = false;
  }, [callState, emitOutcome]);

  // Resolve outcome when the active call changes before reaching idle, then
  // reset per-call health state so the new call starts fresh. Without the
  // reset, call B would inherit call A's desyncReportedRef=true (silencing
  // B's desync checks for the rest of its lifetime) plus stale
  // lastPcStates / pcEverAttached / callStateEnteredAt diagnostics.
  useEffect(() => {
    const tracker = outcomeTrackerRef.current;
    if (tracker && tracker.callId !== callId) {
      emitOutcome('call_changed');
    }
    desyncReportedRef.current = false;
    lastPcStatesRef.current = {};
    lastPcStateChangeAtRef.current = null;
    pcEverAttachedRef.current = false;
    callStateEnteredAtRef.current = Date.now();
  }, [callId, emitOutcome]);

  // Clear any pending timers on unmount so a late-firing stuck outcome can't
  // emit after the hook's lifetime ends.
  useEffect(() => {
    return () => {
      const tracker = outcomeTrackerRef.current;
      if (tracker) {
        clearTimeout(tracker.timeoutId);
        clearInterval(tracker.recoveryIntervalId);
        outcomeTrackerRef.current = null;
      }
    };
  }, []);

  // --- RTCPeerConnection state event listeners ---
  // callState is included in deps so listeners attach when PC becomes available
  // (e.g., null during 'ringing', present after transition to 'in-progress').
  // callStateRef is used inside callbacks to avoid stale closures.
  useEffect(() => {
    if (!isActive || !callId) {
      return;
    }

    let pc: RTCPeerConnection | undefined;
    try {
      pc = vonageClient.getPeerConnection(callId);
    } catch {
      return;
    }

    if (!pc) {
      return;
    }

    pcEverAttachedRef.current = true;

    // Capture initial states
    lastPcStatesRef.current = {
      iceConnectionState: pc.iceConnectionState,
      connectionState: pc.connectionState,
      signalingState: pc.signalingState,
    };
    lastPcStateChangeAtRef.current = Date.now();

    // Log initial state on attach — gives baseline even for calls with no transitions
    log.info('Peer connection state changed', {
      callId,
      event: 'listeners-attached',
      state: pc.connectionState,
      ice_state: pc.iceConnectionState,
      signaling_state: pc.signalingState,
      callState: callStateRef.current,
      timestamp: Date.now(),
    });

    const logStateChange = (event: string, state: string) => {
      lastPcStatesRef.current = {
        ...lastPcStatesRef.current,
        ...(event === 'iceconnectionstatechange' && { iceConnectionState: state }),
        ...(event === 'connectionstatechange' && { connectionState: state }),
        ...(event === 'signalingstatechange' && { signalingState: state }),
      };
      lastPcStateChangeAtRef.current = Date.now();

      log.info('Peer connection state changed', {
        callId,
        event,
        state,
        callState: callStateRef.current,
        timestamp: Date.now(),
      });

      if (['disconnected', 'failed', 'closed'].includes(state)) {
        tracingService.trackEvent(CallEventName.CallHealthAlert, CallEventLevel.Warning, {
          deviceLegId: callId,
          alertType: event,
          state,
        });
      }
    };

    const onIceStateChange = () => {
      logStateChange('iceconnectionstatechange', pc!.iceConnectionState);
    };

    const onConnectionStateChange = () => {
      logStateChange('connectionstatechange', pc!.connectionState);
    };

    const onSignalingStateChange = () => {
      logStateChange('signalingstatechange', pc!.signalingState);
    };

    pc.addEventListener('iceconnectionstatechange', onIceStateChange);
    pc.addEventListener('connectionstatechange', onConnectionStateChange);
    pc.addEventListener('signalingstatechange', onSignalingStateChange);

    return () => {
      pc?.removeEventListener('iceconnectionstatechange', onIceStateChange);
      pc?.removeEventListener('connectionstatechange', onConnectionStateChange);
      pc?.removeEventListener('signalingstatechange', onSignalingStateChange);
    };
  }, [isActive, callId, callState, vonageClient]);

  // --- SDK registry desync check (every 15s + on online/visibility) ---
  // Only runs for states where the PC is expected to exist (see DESYNC_CHECK_STATES).
  useEffect(() => {
    if (!isDesyncCheckActive || !callId) {
      return;
    }

    const currentCallId = callId;

    // Wrapper breaks TS narrowing across the async boundary — the ref value
    // can change during `await`, so each check must re-read fresh.
    const isHangupPending = () => hangupVerificationStatusRef.current === 'pending';

    const checkRegistryDesync = async () => {
      if (desyncReportedRef.current) {
        return;
      }

      // `useVerifiedHangup` is already polling for this call and will emit
      // `call:phantom:detected` on its recovery paths — a second signal from
      // the desync monitor would just be duplicate noise.
      if (isHangupPending()) {
        return;
      }

      let pc: RTCPeerConnection | undefined;
      try {
        pc = vonageClient.getPeerConnection(currentCallId);
      } catch {
        // getPeerConnection threw — treat as undefined
      }

      if (pc) {
        return;
      }

      // First null read — confirm with a second check after 1s
      await new Promise<void>((resolve) => setTimeout(resolve, DESYNC_CONFIRM_DELAY_MS));

      // Bail if call ended or switched to a different call during the delay,
      // or the verified-hangup flow took over.
      if (
        !isActiveRef.current ||
        desyncReportedRef.current ||
        callIdRef.current !== currentCallId ||
        isHangupPending()
      ) {
        return;
      }

      let pcRetry: RTCPeerConnection | undefined;
      try {
        pcRetry = vonageClient.getPeerConnection(currentCallId);
      } catch {
        // Still undefined/thrown
      }

      if (pcRetry) {
        log.info('Registry desync check transient null', {
          device_leg_id: currentCallId,
          result: 'transient_null',
          callState: callStateRef.current,
          timestamp: Date.now(),
        });
        return;
      }

      // Confirmed desync — SDK lost the call
      desyncReportedRef.current = true;

      const desyncId = crypto.randomUUID();
      const desyncStart = Date.now();
      const diagnostics = getVonageSessionDiagnostics();
      const network = getNetworkStatus();
      const callStarted = callStartedAtRef.current;
      const msSinceCallStart =
        callStarted.callId === currentCallId ? desyncStart - callStarted.startedAt : null;
      const msSinceLastPcStateChange = lastPcStateChangeAtRef.current
        ? desyncStart - lastPcStateChangeAtRef.current
        : null;

      const snapshot = {
        desyncId,
        deviceLegId: currentCallId,
        callState: callStateRef.current,
        hangupVerificationStatus: hangupVerificationStatusRef.current,
        lastPcStates: lastPcStatesRef.current,
        pcEverAttached: pcEverAttachedRef.current,
        msInCurrentCallState: desyncStart - callStateEnteredAtRef.current,
        msSinceCallStart,
        msSinceLastPcStateChange,
        vonageInstanceCount: diagnostics.instanceCount,
        totalSessionCreateCount: diagnostics.totalSessionCreateCount,
        sessionsSinceCallStart: diagnostics.sessionsSinceCallStart,
        timeSinceLastSessionCreateMs: diagnostics.timeSinceLastSessionCreateMs,
        networkIsOnline: network.isOnline,
        networkConnectionType: network.connectionType,
        networkQuality: network.quality,
        networkRtt: network.rtt,
        timestamp: desyncStart,
      };

      // WARN, not ERROR — Sentry routing defers to the outcome event. A
      // desync that resolves into `recovered` or `completed_without_pc_recovery`
      // is noise; only `stuck` outcomes escalate.
      log.warn('SDK call registry desync detected', snapshot);
      tracingService.trackEvent(CallEventName.CallHealthDesync, CallEventLevel.Warning, snapshot);

      const tracker: DesyncOutcomeTracker = {
        desyncId,
        callId: currentCallId,
        startedAt: desyncStart,
        pcRecovered: false,
        resolved: false,
        timeoutId: setTimeout(() => {
          emitOutcome('stuck');
        }, DESYNC_OUTCOME_TIMEOUT_MS),
        recoveryIntervalId: setInterval(() => {
          try {
            const maybePc = vonageClient.getPeerConnection(currentCallId);
            if (maybePc) {
              tracker.pcRecovered = true;
            }
          } catch {
            // ignore
          }
        }, DESYNC_RECOVERY_POLL_INTERVAL_MS),
      };
      outcomeTrackerRef.current = tracker;
    };

    const intervalId = setInterval(checkRegistryDesync, REGISTRY_CHECK_INTERVAL_MS);

    const onOnline = () => checkRegistryDesync();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkRegistryDesync();
      }
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);

    checkRegistryDesync();

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isDesyncCheckActive, callId, vonageClient, emitOutcome]);
};
